import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageSquare, Plus, Trash2, Shield, Users, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const MessagingPermissions = () => {
  const { user, token } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const isMountedRef = useRef(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const fetchData = useCallback(async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      const [permRes, usersRes] = await Promise.all([
        axios.get(`${API}/messaging-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (isMountedRef.current) {
        setPermissions(permRes.data);
        setEmployees(usersRes.data.filter(u => u.role === 'employee'));
        setClients(usersRes.data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (isMountedRef.current) toast.error('Erreur lors du chargement des données');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [API, token]);

  useEffect(() => {
    isMountedRef.current = true;
    if (user?.role === 'admin') {
      fetchData();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [user?.role, fetchData]);

  const handleGrantPermission = async () => {
    if (!selectedEmployee || selectedClients.length === 0) {
      toast.error('Veuillez sélectionner un employé et au moins un client');
      return;
    }

    try {
      await axios.post(
        `${API}/messaging-permissions`,
        {
          employee_id: selectedEmployee,
          client_ids: selectedClients
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Permissions accordées avec succès');
      setDialogOpen(false);
      setSelectedEmployee('');
      setSelectedClients([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution des permissions');
    }
  };

  const handleRevokePermission = async (employeeId, clientId = null) => {
    const message = clientId 
      ? 'Révoquer l\'accès pour ce client ?' 
      : 'Révoquer tous les accès de cet employé ?';
    
    if (!window.confirm(message)) return;

    try {
      const url = clientId 
        ? `${API}/messaging-permissions/${employeeId}?client_id=${clientId}`
        : `${API}/messaging-permissions/${employeeId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Permission révoquée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const toggleClientSelection = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  // Get clients not yet assigned to selected employee
  const getAvailableClients = () => {
    if (!selectedEmployee) return clients;
    
    const existingPerm = permissions.find(p => p.employee_id === selectedEmployee);
    if (!existingPerm) return clients;
    
    return clients.filter(c => !existingPerm.client_ids?.includes(c.id));
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Accès réservé aux administrateurs</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              🔐 Permissions de Messagerie
            </h1>
            <p className="text-lg text-gray-600">
              Gérer l&apos;accès aux messages entre employés et clients
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg">
                <Plus className="mr-2 h-5 w-5" />
                Accorder un accès
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Accorder des permissions de messagerie</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Employé</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name} ({emp.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Clients autorisés ({selectedClients.length} sélectionné(s))</Label>
                  <div className="max-h-64 overflow-y-auto border rounded-lg p-4 space-y-2">
                    {getAvailableClients().map((client) => (
                      <div
                        key={client.id}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedClients.includes(client.id)
                            ? 'bg-purple-50 border-2 border-purple-500'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                        onClick={() => toggleClientSelection(client.id)}
                      >
                        <Checkbox
                          checked={selectedClients.includes(client.id)}
                          onChange={() => toggleClientSelection(client.id)}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{client.full_name}</p>
                          <p className="text-sm text-gray-600">{client.email}</p>
                        </div>
                      </div>
                    ))}
                    {getAvailableClients().length === 0 && (
                      <p className="text-center text-gray-500 py-4">
                        Aucun client disponible
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleGrantPermission}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                  disabled={!selectedEmployee || selectedClients.length === 0}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Accorder les permissions
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-xl bg-gradient-to-br from-purple-50 to-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Employés avec accès</p>
                  <p className="text-2xl font-bold text-gray-900">{permissions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total clients</p>
                  <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Accès accordés</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {permissions.reduce((acc, p) => acc + (p.client_ids?.length || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Permissions List */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Permissions actives
            </CardTitle>
          </CardHeader>
          <CardContent>
            {permissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Shield className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucune permission configurée</p>
                <p className="text-sm mt-2">
                  Les employés ne peuvent actuellement voir que les clients qui leur sont assignés via les dossiers
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {permissions.map((perm) => (
                  <div
                    key={perm.employee_id}
                    className="border rounded-xl p-6 bg-gradient-to-r from-gray-50 to-white hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {perm.employee_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {perm.clients?.length || 0} client(s) autorisé(s)
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokePermission(perm.employee_id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Tout révoquer
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {perm.clients?.map((client) => (
                        <Badge
                          key={client.id}
                          variant="secondary"
                          className="px-3 py-1 flex items-center gap-2 bg-purple-100 text-purple-800"
                        >
                          {client.name}
                          <button
                            onClick={() => handleRevokePermission(perm.employee_id, client.id)}
                            className="hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MessagingPermissions;
